import { useState } from 'react'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { showSuccessToast, handleAPIError, TOAST_MESSAGES } from '@/utils/toasts'
import { getUserFacingError } from '@/utils/errorMessages'
import { APIRoutes } from '@/services/api.routes'
import { analytics } from '@/services/analytics'
import { faTimesCircle, faSpinner } from '@/fontawesome'
import PasswordInput from '@/components/forms/PasswordInput'
import ForgotPasswordModal from '@/components/auth/ForgotPasswordModal'
import { useDarkMode } from '@/hooks/useDarkMode'
import DarkModeToggle from '@/components/common/DarkModeToggle'

interface LoginProps {
  onLogin: (token: string) => void
}

export default function Login({ onLogin }: LoginProps) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false)
  const { isDarkMode } = useDarkMode()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const response = await APIRoutes.login(email.trim().toLowerCase(), password)
      analytics.track('Login')
      showSuccessToast(TOAST_MESSAGES.LOGIN_SUCCESS)
      onLogin(response.token)
    } catch (err) {
      setError(getUserFacingError(err, 'E-Mail-Adresse oder Passwort ist leider nicht korrekt. Bitte überprüfe deine Eingaben und versuche es erneut.'))
      handleAPIError(err, TOAST_MESSAGES.LOGIN_ERROR)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 px-4 relative">
      <DarkModeToggle className="absolute top-4 right-4" />
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8 space-y-8">
          <div className="text-center">
            <div className="mb-6">
              <img
                src={isDarkMode ? "/logo_light.png" : "/logo.png"}
                alt="Börsencampus Logo"
                className="h-16 object-contain mx-auto my-6"
              />
              <div className="w-20 h-1 bg-blue-600 mx-auto rounded-full"></div>
            </div>
            <p className="text-gray-600 dark:text-gray-300 text-lg">
              Melde dich jetzt im Börsencampus an.
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Benutzername
                </label>
                <input
                  id="email"
                  type="text"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder="Mail eingeben"
                  autoComplete="username"
                />
              </div>

              <PasswordInput
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Passwort eingeben"
                required
              />
            </div>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <FontAwesomeIcon icon={faTimesCircle} className="h-5 w-5 text-red-400" />
                  </div>
                  <div className="ml-3">
                    <p className="text-red-700 dark:text-red-400 text-sm font-medium">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
                  Anmelden...
                </>
              ) : (
                'Anmelden'
              )}
            </button>
          </form>

          <div className="text-center space-y-3">
            <button
              type="button"
              onClick={() => setIsForgotPasswordOpen(true)}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 font-medium cursor-pointer"
            >
              Passwort vergessen?
            </button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                  oder
                </span>
              </div>
            </div>

            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Noch kein Konto?{' '}
                <Link
                  to="/auth/register"
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 font-medium"
                >
                  Jetzt registrieren
                </Link>
              </p>
            </div>
          </div>

          <div className="text-center pt-4 border-t border-gray-200 dark:border-gray-700 space-y-1">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Powered by SvenBolz Trading Academy
            </p>
            <a
              href="https://www.svenbolz.com/risikohinweis-disclaimer"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300"
            >
              Risikohinweis & Disclaimer
            </a>
          </div>
        </div>
      </div>

      <ForgotPasswordModal
        isOpen={isForgotPasswordOpen}
        onClose={() => setIsForgotPasswordOpen(false)}
      />
    </div>
  )
}
